import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { MessageSquare, Mail } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Story, mapSupabaseStoryToStory } from "../../types/supabase";
import { EdgeGlow } from "../../components/edge-glow";
import { supabase } from "../../lib/supabaseClient";
import { useEffect } from "react";

export function VoicesSection() {
  // Fetch featured stories directly from Supabase
  const { data: stories, isLoading, error } = useQuery<Story[]>({
    queryKey: ['featured-stories'],
    queryFn: async () => {
      console.log('Fetching featured stories for VoicesSection component...');
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('approved', true)
        .eq('featured', true)
        .order('created_at', { ascending: false })
        .limit(2);
        
      if (error) {
        console.error('Error fetching featured stories:', error);
        throw new Error(error.message);
      }
      
      console.log('Featured stories fetched:', data);
      // Map Supabase data to client-side Story type
      return data?.map(mapSupabaseStoryToStory) || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
  
  useEffect(() => {
    if (error) {
      console.error('Error in VoicesSection useQuery:', error);
    }
  }, [error]);

  return (
    <section id="voices" className="py-20 relative z-10 bg-white dark:bg-black">
      <div className="container mx-auto px-6">
        <motion.div 
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center text-black dark:text-white">
            Stories are <span className="text-teal-600 dark:text-teal-400">power</span>. Voices matter.
          </h2>
          
          <motion.p 
            className="text-xl text-center mb-12 text-black dark:text-white"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Elevita is built on lived experiences, not theories. If you've ever felt invisible in care, your story matters. We're gathering real stories to ensure Elevita catches what humans sometimes miss.
          </motion.p>
          
          {/* Story cards */}
          <motion.div 
            className="grid md:grid-cols-2 gap-6 mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {isLoading ? (
              // Display skeleton loader while loading
              <>
                {[1, 2].map((_, index) => (
                  <div key={index} className="bg-gray-900 bg-opacity-60 p-6 rounded-xl border border-gray-800 animate-pulse dark:bg-gray-500">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="w-12 h-12 rounded-full bg-gray-800 flex-shrink-0"></div>
                      <div className="flex-1">
                        <div className="h-5 bg-gray-800 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-gray-800 rounded w-1/3"></div>
                      </div>
                    </div>
                    <div className="h-4 bg-gray-800 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-800 rounded w-5/6 mb-2"></div>
                    <div className="h-4 bg-gray-800 rounded w-11/12 mb-4"></div>
                    <div className="h-3 bg-gray-800 rounded w-1/4"></div>
                  </div>
                ))}
              </>
            ) : stories && stories.length > 0 ? (
              // Display actual database stories
              <>
                {stories.slice(0, 2).map((story: Story) => (
                  <Link key={story.id} href={`/story-direct/${story.id}`}>
                    <div className="bg-white dark:bg-teal-900/50 bg-opacity-60 p-6 rounded-xl border border-gray-800 hover:border-[#14b8a6]/50 transition-colors cursor-pointer">
                      <div className="flex items-start space-x-4 mb-4">
                        <div className="w-12 h-12 rounded-full bg-[#14b8a6]/20 flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="text-[#14b8a6]" size={20} />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-black dark:text-white mb-1">{story.title}</h3>
                          <p className="text-sm text-gray-700 dark:text-gray-400">{story.author_name || 'Anonymous'}</p>
                        </div>
                      </div>
                      <p className="text-gray-700 dark:text-gray-400 mb-4">
                        "{story.excerpt}"
                      </p>
                      <div className="text-[#14b8a6] text-sm">Read full story</div>
                    </div>
                  </Link>
                ))}
                
                {/* If we only have one story, show a "Your Story Here" card */}
                {stories.length === 1 && (
                  <div className="bg-white dark:bg-teal-900/50 bg-opacity-60 p-6 rounded-xl border border-gray-800">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="text-teal-600" size={20} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-black dark:text-white mb-1">Your Story Here</h3>
                        <p className="text-sm text-gray-400">Share your healthcare experience</p>
                      </div>
                    </div>
                    <p className="text-black dark:text-gray-300 mb-4">
                      "Every healthcare journey is unique. Share your experience to help us improve Elevita's ability to recognise important patterns and provide better care."
                    </p>
                    <Link href="/voices#submit-story">
                      <div className="text-[#14b8a6] text-sm cursor-pointer">Submit your story</div>
                    </Link>
                  </div>
                )}
              </>
            ) : (
              // No stories in database message with call-to-action
              <>
                <div className="col-span-2 text-center py-8">
                  <p className="text-gray-700 dark:text-white mb-4">No stories available at the moment.</p>
                  <Link href="/voices#submit-story">
                    <div className="text-teal-600 dark:text-teal-200 text-sm cursor-pointer">Be the first to share your story</div>
                  </Link>
                </div>
              </>
            )}
          </motion.div>
          
          {/* CTA buttons */}
          <motion.div 
            className="flex flex-col md:flex-row gap-6 justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link href="/voices">
              <EdgeGlow color="teal" intensity="medium">
                <Button variant="default" size="lg" className="flex items-center text-black">
                  <MessageSquare className="mr-2" size={18} />
                  Read More Stories
                </Button>
              </EdgeGlow>
            </Link>
            
            <Link href="/voices#submit-story">
              <Button variant="default" size="lg" className="flex items-center text-black">
                <Mail className="mr-2" size={18} />
                Share Your Story
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}